import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const ITEMS = [
  { id: "dog", label: "dog", emoji: "🐶" },
  { id: "cat", label: "cat", emoji: "🐱" },
  { id: "duck", label: "duck", emoji: "🦆" },
  { id: "bunny", label: "bunny", emoji: "🐰" },
  { id: "ball", label: "ball", emoji: "⚽" },
  { id: "apple", label: "apple", emoji: "🍎" },
  { id: "fish", label: "fish", emoji: "🐟" },
  { id: "car", label: "car", emoji: "🚗" },
];

function shuffle(a){ const b=[...a]; for(let i=b.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1)); [b[i],b[j]]=[b[j],b[i]];} return b; }
const pick = (arr) => arr[Math.floor(Math.random()*arr.length)];

export default function FindAfterSwapGame({ revealMs=5000, swapCount=2, optionsCount=4 }){
  const [cards, setCards] = useState([]);
  const [target, setTarget] = useState(null);
  const [phase, setPhase] = useState("setup");
  const [round, setRound] = useState(1);
  const [stars, setStars] = useState(0);
  const [locked, setLocked] = useState(false);

  const targetRef = useRef(target); useEffect(()=>{ targetRef.current = target; }, [target]);
  const timers = useRef([]);
  const later = (ms)=> new Promise(res=> { const t=setTimeout(res, ms); timers.current.push(t); });

  useEffect(()=>{ startRound(); return ()=> { timers.current.forEach(clearTimeout); timers.current=[]; }; }, []);

  useEffect(()=>{
    if (phase === 'feedback') document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
  }, [phase]);

  function startRound(){
    timers.current.forEach(clearTimeout); timers.current=[];
    const count = Math.max(3, Math.min(4, Number(optionsCount)||4));
    const sample = shuffle(ITEMS).slice(0, count);
    const tgt = pick(sample);
    setTarget(tgt);
    setCards(sample.map((c,i)=> ({...c, revealed:true, key:`${c.id}-${i}-${Date.now()}`})));
    setPhase("reveal");
    setLocked(true);
    const t = setTimeout(()=>{
      setCards(prev=> prev.map(c=> ({...c, revealed:false})));
      setPhase("hide");
      setLocked(false);
    }, revealMs);
    timers.current.push(t);
  }

  async function runSwaps(n){
    if(phase!=="hide") return;
    setPhase("swapping");
    for(let k=0;k<n;k++){
      await later(500);
      setCards(prev=>{
        const next=[...prev]; if(next.length<2) return next;
        const a=Math.floor(Math.random()*next.length); let b=Math.floor(Math.random()*next.length); while(b===a) b=Math.floor(Math.random()*next.length);
        [next[a], next[b]] = [next[b], next[a]];
        return next;
      });
    }
    await later(300);
    setPhase("pick");
  }

  function onPick(idx, id) {
  // Allow taps during both 'hide' and 'pick' phases (paper-game behavior)
  if (!((phase === "hide") || (phase === "pick")) || locked) return;
  setLocked(true);

  // Reveal tapped card first
  setCards(prev => prev.map((c, i) => (i === idx ? { ...c, revealed: true } : c)));

  const isRight = !!targetRef.current && id === targetRef.current.id;

  // Small delay so the reveal is visible, then judge
  const t = setTimeout(() => {
    if (isRight) {
      setStars(s => s + 1);
      setPhase("feedback");     // show the modal
      setLocked(false);

      // ✅ Auto-close modal after 1.2s and start next round
      const tAuto = setTimeout(() => {
        setRound(r => r + 1);
        startRound();
      }, 1200);
      timers.current.push(tAuto);
    } else {
      // Wrong: hide the card again after a moment and allow another try
      const tWrong = setTimeout(() => {
        setCards(prev => prev.map((c, i) => (i === idx ? { ...c, revealed: false } : c)));
        setLocked(false);
      }, 700);
      timers.current.push(tWrong);
    }
  }, 180);

  timers.current.push(t);
}
  return (
    <div className="game-root">
      <style>{`
        :root{ --border:#e6e9ee; --shadow:0 8px 24px rgba(16,24,40,.06); --ink:#0f172a; --muted:#5b6474; }
        .game-root{ width:100%; padding:16px; background:#f9fafb; color:var(--ink); font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial; }
        .wrap{ width:100%; max-width:640px; margin:0 auto; }
        .row{ display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
        .stars{ display:flex; align-items:center; gap:8px; color:#f59e0b; }
        .round{ color:var(--muted); font-size:14px; }
        .target{ background:#fff; border:1px solid var(--border); border-radius:16px; box-shadow:var(--shadow); padding:12px; margin-bottom:16px; }
        .target h4{ margin:0 0 8px; text-align:center; font-weight:700; }
        .box{ width:120px; height:120px; margin:0 auto; display:flex; align-items:center; justify-content:center; border:2px solid var(--border); border-radius:16px; }
        .cap{ text-align:center; color:var(--muted); font-size:14px; margin-top:8px; }
        .grid{ display:grid; grid-template-columns:repeat(2,1fr); gap:12px; margin-bottom:16px; }
        .card{ display:flex; align-items:center; justify-content:center; height:140px; border-radius:16px; border:2px solid var(--border); box-shadow:var(--shadow); background:#fff; cursor:pointer; }
        .q{ font-size:64px; color:#ef4444; opacity:.9; }
        .emoji{ font-size:72px; }
        .btn{ padding:10px 16px; font-weight:700; background:#fff; border:1px solid var(--border); border-radius:12px; box-shadow:var(--shadow); cursor:pointer; }
        .controls{ display:flex; align-items:center; gap:12px; flex-wrap:wrap; justify-content:center; }
        .tm2-overlay{ position:fixed; top:0; left:0; right:0; bottom:0; background:rgba(0,0,0,0.45); display:flex; align-items:center; justify-content:center; z-index:2147483647; }
        .tm2-dialog{ background:#fff; border-radius:20px; padding:24px 28px; box-shadow:0 16px 40px rgba(0,0,0,0.25); text-align:center; max-width:300px; } .tm2-dialog h2{ margin:0; font-size:26px; color:#16a34a; } .tm2-dialog p{ font-size:16px; color:#555; margin:8px 0 16px; }
        .modal h2{ margin:0; font-size:26px; color:#16a34a; }
        .modal p{ font-size:16px; color:#555; margin:8px 0 16px; }
      `}</style>

      <div className="wrap">
        <div className="row"><div className="stars"><span style={{fontSize:22}}>⭐</span><strong>{stars}</strong></div><div className="round">Round {round}</div></div>

        <div className="target">
          <h4>Find this picture:</h4>
          <div className="box"><span style={{fontSize:72}}>{target?.emoji ?? ''}</span></div>
          <div className="cap">"Where is the {target?.label}? Tap it!"</div>
        </div>

        <div className="grid">
          {cards.map((c, i)=> (
            <button key={c.key} className="card" disabled={locked}
              onClick={()=> onPick(i, c.id)} aria-label={`card ${i+1}`}>
              {!c.revealed ? <span className="q">?</span> : <span className="emoji">{c.emoji}</span>}
            </button>
          ))}
        </div>

        <div className="controls">
          {phase==='hide' && (<button className="btn" onClick={()=> runSwaps(swapCount)}>Start shuffle</button>)}
        </div>
      </div>

      {phase==='feedback' && typeof document!=='undefined' && createPortal(
        <div className="tm2-overlay" role="dialog" aria-modal="true" aria-label="Great job">
          <div className="tm2-dialog">
            <div style={{ fontSize:64, marginBottom:12 }}>🎉</div>
            <h2>Great job!</h2>
            <p>You found the {target?.label}!</p>
            
          </div>
        </div>, document.body)}
    </div>
  );
}
